import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, BookOpen } from "lucide-react";
import { assetUrl, lines } from "@/lib/api";

const hexToRgb = (hex) => {
  const h = (hex || "#1F1C1B").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) || 0);
};

export const HomeHero = ({ h }) => {
  const [r, g, b] = hexToRgb(h.panel_color);
  const op = Number(h.panel_opacity);
  const light = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 && op > 0.45;
  const txt = light ? "text-charcoal" : "text-white";
  return (
    <section className="relative overflow-hidden bg-charcoal" data-testid="home-hero">
      <img src={assetUrl(h.bg_image)} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: Number(h.bg_opacity) }} data-testid="hero-bg" />
      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-24 grid gap-10 lg:grid-cols-[minmax(0,1fr)_auto] items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className={`min-w-0 rounded-2xl backdrop-blur-[3px] p-6 sm:p-9 lg:p-10 ${txt}`} style={{ backgroundColor: `rgba(${r},${g},${b},${op})` }} data-testid="hero-panel">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.22em] text-brass" data-testid="hero-label">{h.label}</p>
          <h1 className="mt-3 font-serif font-bold leading-tight text-3xl sm:text-4xl lg:text-[2.1rem] xl:text-[2.6rem] lg:whitespace-nowrap break-words" data-testid="hero-title">{h.title}</h1>
          <p className="mt-4 text-sm sm:text-base leading-relaxed opacity-90 max-w-2xl" data-testid="hero-subtitle">{h.subtitle}</p>
          <ul className="mt-5 space-y-2">
            {lines(h.bullets).map((x, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm sm:text-[15px]" data-testid={`hero-bullet-${i}`}><CheckCircle2 className="h-5 w-5 text-brass shrink-0 mt-px" />{x}</li>
            ))}
          </ul>
          <p className="mt-5 font-serif italic text-brass text-base sm:text-lg" data-testid="hero-tagline">{h.tagline}</p>
          <span className="mt-5 inline-block rounded-full border border-brass/60 px-4 py-1.5 text-xs sm:text-sm tracking-wide" data-testid="hero-pill">{h.pill}</span>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/digital-edition" className="rounded-full bg-brand hover:bg-brand-dark text-white px-6 py-3 text-sm font-semibold transition-colors duration-200" data-testid="hero-digital-button">{h.btn_primary}</Link>
            <Link to="/listing-options" className={`rounded-full border-2 px-6 py-3 text-sm font-semibold transition-colors duration-200 ${light ? "border-charcoal hover:bg-charcoal hover:text-white" : "border-white hover:bg-white hover:text-charcoal"}`} data-testid="hero-list-button">{h.btn_secondary}</Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="justify-self-center">
          <Link to="/digital-edition" className="relative block group" data-testid="hero-cover-link">
            <img src={assetUrl(h.cover_image)} alt="ISD Cover" className="w-64 sm:w-80 lg:w-72 xl:w-80 2xl:w-[26rem] h-auto rounded-md shadow-2xl transition-transform duration-300 group-hover:-translate-y-1" />
            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand text-white text-xs font-bold px-4 py-2 shadow-lg flex items-center gap-1.5" data-testid="hero-cover-badge">
              <BookOpen className="h-3.5 w-3.5" />{h.cover_badge}
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
